import React, { useState, useCallback, useEffect} from 'react';
import { Container, Card } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { startOfWeek, endOfWeek, addWeeks } from 'date-fns';

import { Confirmation, SelectUser, SelectService, SelectBookingTime, SelectTokens, ErrorDiv } from './components';
import MultiStep from '../../../components/common/MultiStep';
import SubHeader from '../../../components/common/SubHeader';

import { findMinMax, findDateRanges, adjustEventConflictData, handleFormatBooking } from '../../../utils/eventServicesUtils';
import { generateServiceBlockTimeslots } from '../../../utils/dates';
import { addToCart } from '../../../utils/thunks';
import * as actions from '../../../store/actions';

import POS from '../../../api/Pos';
import Services from '../../../api/Services';
import Events from '../../../api/Events';
import Locations from '../../../api/Locations';

export const BookForOthers = ({
    initialCustomer=null,
    fromPOS=false,
    handleClose=null,
    registerId=null,
    ...props
})=>{

    const dispatch = useDispatch();
    const location = useLocation();
    // the service page's "Book a User" links here with ?service=<id>: start on that service and skip the service step
    const preselectedServiceId = fromPOS ? null : (parseInt(new URLSearchParams(location.search).get("service")) || null);

    const [errors, setErrors]=useState({users: [], service: [], booking: [], tokens: [], general: []});
    const [success, setSuccess]=useState(null);
    const [services, setServices] = useState([]);
    const [activeService, setActiveService]=useState(null);
    const [dateWeek, setDateWeek]=useState(new Date());
    const [selectedUser, setSelectedUser]=useState(initialCustomer ? initialCustomer : null);
    const [userTokens, setUserTokens]=useState([]);
    const [selectedBookings, setSelectedBookings]=useState([]);
    const [selectedLocation, setSelectedLocation]=useState(null);
    const [matchedTokens, setMatchedTokens]=useState({text: <></>, matched: []});
    const [conflictEvents, setConflictEvents]=useState(null);
    const [selectedProduct, setSelectedProduct]=useState([]);
    const [goToName, setGoToName]=useState(null);
    const [stepName, setStepName]=useState("");
    const [lateBooking, setLateBooking]=useState(false); // staff booking a session that already happened

    const goTo = useCallback(name =>{
        setGoToName(name);
        setTimeout(() => { setGoToName(null); }, 1000);
    },[])

    const handleError=(errorType, error)=>{
        let newErrors = {...errors};
        if( error === null) {
            newErrors[errorType] = [];
        }else{
            newErrors[errorType] = error;
        }
        setErrors(newErrors);
    }

    const validate = (pageName)=>{
        let error={users: [], service: [], booking: [], tokens: []};
        let validated = true;
        switch(pageName){
            case'SelectUser':
                if(!selectedUser) {
                    error.users.push("You must select a user.");
                }
                handleError("users", error.users)
                break;
            case'SelectService':
                if(!activeService){
                    error.service.push("You must select a service.");
                }
                handleError("service", error.service)
                break;
            case'SelectBookingTime':
                if(!selectedLocation?.id){
                    error.booking.push("Please make sure a location is selected!");
                }
                if(selectedBookings.length < activeService.min_timeslots){
                    error.booking.push(`Please select at least ${activeService.min_timeslots} timeslots.`);
                }
                handleError("booking", error.booking)
                break;
            case'SelectTokens':
                if(matchedTokens?.length < selectedBookings.length && !selectedProduct?.length){
                    error.tokens.push("If using tokens a user already has, please make sure they have enough.");
                }else if(!selectedProduct?.length){
                    error.tokens.push("Please select a token to purchase.");
                }
                handleError("tokens", error.tokens)
                break;
            default:
                error={users: [], service: [], booking: [], tokens: []}
                break;
        }
        if(error.users.length > 0 || error.service.length > 0 || error.booking.length > 0 || error.tokens.length > 0)validated = false; 
        return validated;
    }

    const setBlocks = (service)=>{
        let newBlocks = generateServiceBlockTimeslots(service);
        service.blocks = newBlocks;
    }

    const handleBooking=async(token)=>{
        let bookingObject = await handleFormatBooking(token, selectedBookings, activeService, selectedUser);
        try{
            let response = await POS.payment.service_tokens(bookingObject);
            if(response.status === 200 && response.data === "Successful") {
                    setSuccess("Booking");
                    // if(fromPOS && handleClose) handleClose(); //can add back later if the process feels too slow on the POS
                    // else goTo("Confirmation");
                    goTo("Confirmation")
                }
        }catch(ex){
            console.error(ex)
            handleError(ex)
        }
    }

    const handleLateBooking = async ({ paymentMode, variantId, reason })=>{
        let bookingObject = await handleFormatBooking({ variant_id: variantId || undefined }, selectedBookings, activeService, selectedUser);
        try{
            let response = await Services.lateBooking({ ...bookingObject, payment_mode: paymentMode, reason: reason });
            if(response.status === 200 && !response.errors) {
                setSuccess("LateBooking");
                goTo("Confirmation");
            }
            else handleError("tokens", response.errors || ["Unknown error booking the session"])
        }catch(ex){
            console.error(ex)
            handleError("tokens", ["Unknown error booking the session"])
        }
    }

    const handleLateBookingChange = (isLate)=>{
        setLateBooking(isLate);
        setSelectedBookings([]);
        if(!isLate) setDateWeek(new Date());
    }

    const makeBooking = async (bookingObject)=>{
        try{
            let response = await Services.createBooking(bookingObject);
            if(response.status === 200) {
                setSuccess("Booking");
                goTo("Confirmation");
                return response.data;
            }
            else if(response.errors) handleError(response.errors)
            else handleError("Unknown error creating booking")
        }catch(ex){
            console.error(ex)
            handleError(ex)
        }
    }

    const checkTokensToBuy=(amount, matches, selectedBookings)=>{
        if(amount === "all") return selectedBookings.length;
        else if(amount === "some") return selectedBookings.length - matches[0]?.number_of_tokens;
        else if(amount === "one") return 1;  
        else return selectedBookings.length;
    }

    /** the ability to add to cart won't be available unless the registerId and fromPOS are passed into this component so we don't have to check that we have them in this funciton */
    const handleAddToCart = async(product, amount)=>{
        let usedProduct = product ? product : selectedProduct;
        if(!usedProduct.hasOwnProperty("variant_id")) usedProduct.variant_id = usedProduct.variants[0].id;
        let bookingObject = await handleFormatBooking(usedProduct, selectedBookings, activeService, selectedUser);
        let data = await makeBooking(bookingObject);
        let matches = matchedTokens?.filter((token)=>token.id === usedProduct.id);
        let tokensToBuy = checkTokensToBuy(amount, matches, selectedBookings)
        let products = [];
        for(let i=0; tokensToBuy > i; i++){
            let item = {
                product_name: usedProduct.name,
                product_id: usedProduct.id,
                variant_id: usedProduct.variants[0].id,
                variant_name: usedProduct.variants[0].name,
                qty: 1,
                event:{
                    event_id: data.id,
                    for_user_id: selectedUser?.id
                }
            }
            products.push(item)
        }
        dispatch(addToCart(products, false, registerId, selectedUser.id));
        dispatch(actions.orderBookingDetails({id: data.id, bookingObject: bookingObject, bookingSlots: selectedBookings}, registerId));
        setSuccess("Cart");
        goTo("Confirmation");
        if(handleClose) handleClose();
        //gotta close the modal in the pos
    }

    const checkServiceAndUserTokens=(userTokens, tokensNeeded, slots)=>{   
        if(userTokens && tokensNeeded && slots){
            let matched=[]
            
            tokensNeeded.forEach(token =>{
                userTokens.forEach((userToken)=>{
                    if(token?.id === userToken?.id) matched.push(userToken);
                })
            })
    
            setMatchedTokens(matched);
        }
    }

    const saveStepName = (name)=>{
        setStepName(name)
    }

    const getEventConflicts = async(blocks, locations)=>{
        const functionBlocks = blocks ? blocks : activeService?.blocks;
        const functionLocations = locations ? locations : selectedLocation?.id;

        let minMax = findMinMax(functionBlocks);
        let list = findDateRanges(functionBlocks, startOfWeek(dateWeek), endOfWeek(dateWeek), minMax, lateBooking);
        // if nothing comes back here, that means there are no bookings available in what's left of the week.
        // We don't have a service without bookings, so we can assume that just means they only have them on Mondays
        // and we're later in the week or something.  Look at next week instead
        if(!list?.length && !lateBooking){
            list = findDateRanges(functionBlocks, startOfWeek(addWeeks(dateWeek, 1)), endOfWeek(addWeeks(dateWeek, 1)), minMax);
        }
        try{
            let response = await Events.getByLocationAndDate({
                location_ids: [functionLocations],
                datetime_ranges: list,
                include_meta_events: 0
            })
            if(response.status === 200 && response.data?.length) setConflictEvents(adjustEventConflictData(functionLocations, response.data))
            else if (response.errors) {
                handleError("general", response.errors)
                setConflictEvents(null)
            }
            else {
                handleError("general", "Unknown error getting conflicts")
                setConflictEvents(null)
            }
        }catch(ex){
            console.error(ex);
            handleError("general", ex)
        }
    }

    // a late booking pages back through past weeks, so load that week's bookings each time
    useEffect(()=>{
        if(lateBooking && activeService && selectedLocation?.id) getEventConflicts(null, selectedLocation.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[lateBooking, dateWeek]);

    // load the linked service the way SelectService does when staff pick one
    useEffect(()=>{
        const preselectService = async()=>{
            try{
                let serviceResponse = await Services.get({ id: preselectedServiceId });
                const service = serviceResponse?.data?.services?.[0];
                if(!service) {
                    handleError("general", ["That service couldn't be found. Pick one from Book Services for Others in the menu instead."]);
                    return;
                }
                setBlocks(service);
                let response = await Locations.get(); // can't filter by id, so get them all and filter
                if(response.data && response.status === 200) {
                    const locations = response.data.filter(loc => service.location_ids?.includes(loc.id));
                    service.location_info = locations;
                    setSelectedLocation(locations[0]); // the first one; it can be changed on the time step
                    await getEventConflicts(service.blocks, locations[0]?.id);
                    setActiveService(service);
                }
                else if (response.errors) handleError("general", response.errors)
                else handleError("general", "Unknown error getting locations")
            }catch(ex){
                console.error(ex);
                handleError("general", ex)
            }
        }

        if(preselectedServiceId) preselectService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[preselectedServiceId]);

    const skippedSteps = preselectedServiceId ? ['SelectService'] : [];

    const stepOrder = [
        'SelectUser',
        'SelectService',
        'SelectBookingTime',
        'SelectTokens',
        'Confirmation'
    ].filter(name => !skippedSteps.includes(name))

    const steps = [
        {
            name: 'SelectUser', 
            icon: "far fa-user",
            component: 
                <SelectUser 
                    setSelectedUser={setSelectedUser}
                    selectedUser={selectedUser}
                    checkServiceAndUserTokens={checkServiceAndUserTokens}
                    activeService={activeService}
                    userTokens={userTokens}
                    setUserTokens={setUserTokens}
                    selectedBookings={selectedBookings}
                    handleError={handleError}
                    fromPOS={fromPOS}
                    registerId={registerId}
                    errors={errors.users}
                />
        },
        {
            name: 'SelectService', 
            icon: "far fa-mountains",
            component: 
                <SelectService 
                    setServices={setServices}
                    services={services}
                    activeService={activeService}
                    getEventConflicts={getEventConflicts}
                    handleError={handleError} 
                    setBlocks={setBlocks}
                    setSelectedLocation={setSelectedLocation}
                    setActiveService={setActiveService}
                    checkServiceAndUserTokens={checkServiceAndUserTokens}
                    userTokens={userTokens}
                    selectedBookings={selectedBookings}
                    errors={errors.service}
                />
            
        },
        {
            name: 'SelectBookingTime', 
            icon: "far fa-calendar-star",
            component: 
                <SelectBookingTime 
                    setSelectedBookings={setSelectedBookings}
                    selectedBookings={selectedBookings}
                    checkServiceAndUserTokens={checkServiceAndUserTokens}
                    userTokens={userTokens}
                    activeService={activeService}
                    setDateWeek={setDateWeek}
                    dateWeek={dateWeek}
                    setSelectedLocation={setSelectedLocation}
                    selectedUser={selectedUser}
                    selectedLocation={selectedLocation}
                    getEventConflicts={getEventConflicts}
                    conflictEvents={conflictEvents}
                    handleError={handleError}
                    errors={errors.booking}
                    lateBooking={lateBooking}
                    onLateBookingChange={handleLateBookingChange}
                />
        },
        {
            name: 'SelectTokens', 
            icon: "far fa-coins",
            component: 
                <SelectTokens 
                    checkServiceAndUserTokens={checkServiceAndUserTokens}
                    userTokens={userTokens}
                    activeService={activeService}
                    selectedBookings={selectedBookings}
                    setSelectedProduct={setSelectedProduct}
                    selectedUser={selectedUser}
                    handleError={handleError} 
                    matchedTokens={matchedTokens}
                    slots={selectedBookings}
                    handleBooking={handleBooking}
                    handleAddToCart={handleAddToCart}
                    registerId={registerId}
                    fromPOS={fromPOS}
                    errors={errors.tokens}
                    lateBooking={lateBooking}
                    handleLateBooking={handleLateBooking}
                />
        },
        {
            name: 'Confirmation', 
            icon: "far fa-check-circle",
            component: 
                <Confirmation 
                    fromPOS={fromPOS}
                    handleClose={handleClose}
                    success={success}
                    activeService={activeService}
                    selectedBookings={selectedBookings}
                    selectedLocation={selectedLocation}
                />
        }
    ].filter(step => !skippedSteps.includes(step.name))

    const mainBody = 
        <Card className={fromPOS ? "" : "content-card"}>
            <MultiStep 
                showNavigation={stepName === "Confirmation" ? false : true}
                goTo={goToName}
                steps ={steps}
                stepOrder={stepOrder}
                saveStep={saveStepName}
                onChangeStep={validate}
                topError={true}
                error={errors.all}
                lastStepName="Close"
                // onSubmit={handleClose ? handleClose : null}
                hideButtons={stepName === "Confirmation" ? true : false} 
                // the late payment step books with its own button; Next there can only fail validation
                hideNext={lateBooking && stepName === "SelectTokens"}
            />
            <ErrorDiv errors={errors.general} />
        </Card>
    

    return(
        <>
            {fromPOS ? <>{mainBody}</> : 
                <Container fluid>
                    <SubHeader items={[
                        { linkAs: Link, linkProps: { to: "/p/home" }, text: "Home" },
                        // came from a service page: offer the way back to it
                        ...(preselectedServiceId ? [
                            { linkAs: Link, linkProps: { to: "/p/services/dashboard" }, text: "Services Dashboard" },
                            { linkAs: Link, linkProps: { to: `/p/services/${preselectedServiceId}` }, text: activeService?.name || "Service" },
                        ] : []),
                        { text: "Book Services for Others" }
                    ]} />
                    {mainBody}
                </Container>
            }
        
        </>
        
    )
}